import { useState, useCallback } from "react"
import { useQuery, useMutation, useSubscription } from "urql"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { Download, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { BulkActionBar } from "@/components/shared/bulk-action-bar"
import { EventsQuery, SourcesQuery } from "@/lib/graphql/queries"
import { BulkDeleteEventsMutation } from "@/lib/graphql/mutations"
import { EventReceivedSubscription } from "@/lib/graphql/subscriptions"
import { useAuth } from "@/hooks/use-auth"
import { exportJSON, exportCSV } from "@/lib/export"
import type { ResultOf } from "@/lib/graphql/types"
import type { EventStatus } from "@/lib/generated/graphql"

type EventItem = ResultOf<typeof EventsQuery>["events"]["nodes"][number]

const LIMIT = 25

const columns: Column<EventItem>[] = [
  {
    header: "Event Type",
    cell: (row) => <code className="text-xs font-mono">{row.eventType}</code>,
  },
  {
    header: "Source",
    cell: (row) => <span className="text-xs">{row.source.slug}</span>,
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Idempotency Key",
    cell: (row) => (
      <code className="text-2xs font-mono text-muted-foreground max-w-50 truncate block">
        {row.idempotencyKey}
      </code>
    ),
  },
  {
    header: "Received",
    cell: (row) => <TimeAgo date={row.receivedAt} />,
  },
]

export function EventsPage() {
  const { isConfigured } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState("")
  const [payloadSearchInput, setPayloadSearchInput] = useState("")
  const [eventTypeInput, setEventTypeInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)
  const debouncedPayloadSearch = useDebouncedValue(payloadSearchInput)
  const debouncedEventType = useDebouncedValue(eventTypeInput)

  const sourceId = searchParams.get("sourceId") ?? undefined
  const status = (searchParams.get("status") as EventStatus) || undefined
  const search = debouncedSearch || undefined
  const payloadSearch = debouncedPayloadSearch || undefined
  const eventType = debouncedEventType || undefined
  const pagination = useCursorPagination(LIMIT, [sourceId, status, search, payloadSearch, eventType])

  const [sources] = useQuery({ query: SourcesQuery })
  const [result, reexecute] = useQuery({
    query: EventsQuery,
    variables: {
      sourceId,
      eventType,
      status,
      search,
      payloadSearch,
      after: pagination.after,
      first: pagination.first,
    },
  })

  const handleNewEvent = useCallback(
    (
      _prev: ResultOf<typeof EventReceivedSubscription> | undefined,
      response: ResultOf<typeof EventReceivedSubscription>,
    ) => {
      if (response?.eventReceived) {
        reexecute({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecute],
  )

  useSubscription({ query: EventReceivedSubscription, pause: !isConfigured }, handleNewEvent)

  const [, bulkDeleteEvents] = useMutation(BulkDeleteEventsMutation)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const handleBulkDelete = async () => {
    setBulkDeleting(true)
    const res = await bulkDeleteEvents({ ids: Array.from(selectedKeys) })
    setBulkDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success(`Deleted ${res.data?.bulkDeleteEvents ?? 0} events`)
    setSelectedKeys(new Set())
    setBulkDeleteOpen(false)
    reexecute({ requestPolicy: "network-only" })
  }

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set(key, value)
    } else {
      params.delete(key)
    }
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Events" description="Received webhook events">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="h-3.5 w-3.5 mr-1.5" />Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => {
              const rows = (result.data?.events.nodes ?? []).map((e) => ({
                id: e.id, eventType: e.eventType, source: e.source.slug,
                status: e.status, idempotencyKey: e.idempotencyKey, receivedAt: e.receivedAt,
              }))
              exportJSON(rows, "events")
            }}>Export JSON</DropdownMenuItem>
            <DropdownMenuItem onClick={() => {
              const rows = (result.data?.events.nodes ?? []).map((e) => ({
                id: e.id, eventType: e.eventType, source: e.source.slug,
                status: e.status, idempotencyKey: e.idempotencyKey, receivedAt: e.receivedAt,
              }))
              exportCSV(rows, ["id", "eventType", "source", "status", "idempotencyKey", "receivedAt"], "events")
            }}>Export CSV</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Select
          value={sourceId ?? "all"}
          onValueChange={(v) => setFilter("sourceId", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-45">
            <SelectValue placeholder="All sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {sources.data?.sources.nodes.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={status ?? "all"}
          onValueChange={(v) => setFilter("status", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-35">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="DELIVERED">Delivered</SelectItem>
            <SelectItem value="FAILED">Failed</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Event type..."
          value={eventTypeInput}
          onChange={(e) => setEventTypeInput(e.target.value)}
          className="w-40 text-sm font-mono"
        />

        <Input
          placeholder="Search events..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />

        <Input
          placeholder="Search payload..."
          value={payloadSearchInput}
          onChange={(e) => setPayloadSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <BulkActionBar count={selectedKeys.size} onClear={() => setSelectedKeys(new Set())}>
        <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Delete
        </Button>
      </BulkActionBar>

      <DataTable
        columns={columns}
        data={result.data?.events.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.events.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No events"
        emptyDescription="Events will appear here when webhooks are received"
        onRowClick={(row) => navigate(`/events/${row.id}`)}
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title="Delete Events"
        description={`This will permanently delete ${selectedKeys.size} selected events and all associated data.`}
        confirmLabel="Delete"
        onConfirm={handleBulkDelete}
        loading={bulkDeleting}
        destructive
      />
    </div>
  )
}
